import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_INTEGRATIONS, GetIntegrationsData } from '@queries/integrations';

// Normalize to lowercase kebab-case like the BE model enforces
const toSlug = (val: unknown): string => {
  const s = String(val ?? '')
    .trim()
    .toLowerCase();
  return s
    .replace(/[._\s]+/g, '-') // dots/underscores/spaces → hyphen
    .replace(/[^a-z0-9-]/g, '') // remove non-alphanumeric/hyphen
    .replace(/-+/g, '-') // collapse hyphens
    .replace(/^-|-$/g, ''); // trim leading/trailing hyphens
};

// Canonicalize known provider families to a base slug for simpler gating.
// Example: "travefy-pro" -> "travefy", "seated-app" -> "seated".
const canonicalize = (slug: string): string => {
  if (slug.startsWith('travefy')) return 'travefy';
  if (slug.startsWith('seated')) return 'seated';
  return slug;
};

export const useIntegrations = () => {
  const { data, loading, error, refetch } = useQuery<GetIntegrationsData>(GET_INTEGRATIONS, {
    fetchPolicy: 'cache-first',
  });

  const providers = useMemo<string[]>(() => {
    const arr = data?.getIntegrations ?? [];
    const normalized = arr.map((p) => canonicalize(toSlug(p))).filter(Boolean);
    // de-duplicate
    return Array.from(new Set(normalized));
  }, [data?.getIntegrations]);

  const hasIntegration = (provider: string) => providers.includes(canonicalize(toSlug(provider)));

  return { providers, hasIntegration, loading, error, refetch };
};
